/**
 * Shared GraphQL Queries
 *
 * This file contains reusable GraphQL query definitions for consistent usage
 * across components. Import these queries instead of defining them inline.
 *
 * Usage:
 *   import { ROUTINE_DATE_QUERY, AGENDA_GOALS_QUERY } from '@/composables/graphql/queries';
 *
 *   apollo: {
 *     tasklist: {
 *       query: ROUTINE_DATE_QUERY,
 *       ...
 *     }
 *   }
 */
import gql from 'graphql-tag';

// ============================================================================
// ROUTINE QUERIES
// ============================================================================

/**
 * Get routine for a specific date - full version with all task fields
 * Used in: AgendaTime.vue
 */
export const ROUTINE_DATE_QUERY = gql`
  query getRoutineDate($date: String!) {
    routineDate(date: $date) {
      id
      date
      skip
      tasklist {
        id
        name
        description
        time
        points
        ticked
        passed
        wait
        redeemed
        passedPoints
        startEvent
        endEvent
        tags
        steps {
          name
        }
        stimuli {
          name
          splitRate
          earned
        }
      }
    }
  }
`;

/**
 * Get aggregated D/K/G stimulus totals for each day of the week
 * Used in: WeekdaySelectorContainer.vue
 */
export const WEEK_STIMULI_QUERY = gql`
  query weekStimuli($date: String!) {
    weekStimuli(date: $date) {
      date
      D
      K
      G
      skipped
    }
  }
`;

// ============================================================================
// XP / POINTS QUERIES
// ============================================================================

/**
 * Current points balance. Settled server-side (lazy) on every call —
 * today's earnings show as pendingToday and become available tomorrow.
 * Used in: DesktopLayout.vue, MobileLayout.vue
 */
export const XP_BALANCE_QUERY = gql`
  query xpBalance {
    xpBalance {
      earned
      used
      available
      pendingToday
      entitled
    }
  }
`;

/**
 * Redeem a passed (missed) routine task from today using points.
 * Server validates the day window, the frozen price and the balance.
 * Used in: RoutineFocus.vue
 */
export const REDEEM_ROUTINE_ITEM_MUTATION = gql`
  mutation redeemRoutineItem($id: ID!, $taskId: String!, $date: String!) {
    redeemRoutineItem(id: $id, taskId: $taskId, date: $date) {
      routine {
        id
        date
        skip
        tasklist {
          id
          name
          description
          time
          points
          ticked
          passed
          wait
          redeemed
          passedPoints
          startEvent
          endEvent
          tags
          steps {
            name
          }
          stimuli {
            name
            splitRate
            earned
          }
        }
      }
      balance {
        earned
        used
        available
        pendingToday
        entitled
      }
    }
  }
`;

// ============================================================================
// GOAL QUERIES
// ============================================================================

/**
 * Get agenda goals for a specific date - all periods (day, week, month, year, lifetime)
 * Used in: AgendaTime.vue, GoalsCascadeContainer.vue
 *
 * `milestonesTotal` / `milestonesComplete` are the server's own tally for a
 * period goal, filled by the same `autoCheckTaskPeriod` pass that fills
 * `progress` — the figures its auto-tick gate counts in. The Goals screen's
 * week and month rows render them, so one week goal cannot report a different
 * counter there and on the Routine Focus WEEK tab (D-04).
 */
export const AGENDA_GOALS_QUERY = gql`
  query agendaGoals($date: String!) {
    agendaGoals(date: $date) {
      id
      date
      period
      goalItems {
        id
        body
        progress
        milestonesTotal
        milestonesComplete
        isComplete
        isMilestone
        taskRef
        goalRef
        contribution
        reward
        tags
        status
        completedAt
        subTasks {
          id
          body
          isComplete
        }
      }
    }
  }
`;

/**
 * Get daily goals with full details (optimized: scoped fetch + parallel queries)
 * Used in: RoutineFocus.vue, useApolloCacheUpdates.js, goalItemQueries.js,
 * priorityQueries.js
 */
export const DAILY_GOALS_QUERY = gql`
  query optimizedDailyGoals($date: String!) {
    optimizedDailyGoals(date: $date) {
      id
      date
      period
      goalItems {
        id
        body
        progress
        milestonesTotal
        milestonesComplete
        milestoneDays {
          date
          status
        }
        isComplete
        taskRef
        goalRef
        isMilestone
        contribution
        reward
        tags
        status
        completedAt
        subTasks {
          id
          body
          isComplete
        }
      }
    }
  }
`;

/**
 * Get goals for a specific period and date - minimal version for dropdowns
 * Used in: GoalCreationContainer.vue, AiSearchModalContainer.vue
 */
export const GOAL_DATE_PERIOD_MINIMAL_QUERY = gql`
  query goalDatePeriod($period: String!, $date: String!) {
    goalDatePeriod(period: $period, date: $date) {
      id
      date
      period
      goalItems {
        id
        body
      }
    }
  }
`;

/**
 * Get goals for a specific period and date - with task ref and completion
 * Used in: AiSearchModalContainer.vue
 */
export const GOAL_DATE_PERIOD_QUERY = gql`
  query goalDatePeriod($period: String!, $date: String!) {
    goalDatePeriod(period: $period, date: $date) {
      id
      date
      period
      goalItems {
        id
        body
        contribution
        taskRef
        tags
        isComplete
      }
    }
  }
`;

/**
 * Get goals by goal reference - for milestone relationships
 * Used in: AiSearchModalContainer.vue
 */
export const GOALS_BY_GOAL_REF_QUERY = gql`
  query goalsByGoalRef($goalRef: String!) {
    goalsByGoalRef(goalRef: $goalRef) {
      id
      date
      period
      goalItems {
        id
        body
        isComplete
        goalRef
        taskRef
        tags
        isMilestone
      }
    }
  }
`;

// ============================================================================
// USER/GROUP QUERIES
// ============================================================================

/**
 * Get users by group ID
 * Used in: FamilyRoutine.vue
 */
export const GET_USERS_BY_GROUP_ID_QUERY = gql`
  query getUsersByGroupId($groupId: String!) {
    getUsersByGroupId(groupId: $groupId) {
      email
      name
      picture
    }
  }
`;

/**
 * Get routines by group email
 * Used in: FamilyRoutine.vue
 */
export const ROUTINES_BY_GROUP_EMAIL_QUERY = gql`
  query routinesByGroupEmail($groupId: String!, $email: String!) {
    routinesByGroupEmail(groupId: $groupId, email: $email) {
      id
      date
      tasklist {
        id
        name
        time
        points
        ticked
        passed
        stimuli {
          name
          splitRate
          earned
        }
      }
    }
  }
`;

// ============================================================================
// PROGRESS QUERIES
// ============================================================================

/**
 * Get progress data
 * Used in: ProgressTime.vue
 */
export const GET_PROGRESS_QUERY = gql`
  query getProgress($period: String!, $startDate: String!, $endDate: String!) {
    getProgress(period: $period, startDate: $startDate, endDate: $endDate) {
      date
      period
      totalPoints
      earnedPoints
      completedTasks
      totalTasks
    }
  }
`;

// ============================================================================
// YEAR GOALS QUERIES
// ============================================================================

/**
 * Get current year goal by ID
 * Used in: YearGoalsTime.vue
 */
export const CURRENT_YEAR_GOAL_QUERY = gql`
  query currentYearGoal($id: ID!) {
    currentYearGoal(id: $id) {
      id
      body
      date
      period
      isComplete
      isMilestone
      goalRef
      taskRef
      tags
      status
      contribution
      milestones {
        id
        body
        date
        period
        isComplete
        status
      }
    }
  }
`;
